import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import { getMySubmissions } from "../services/exams";
import { motion } from "framer-motion";
import { 
  Trophy, 
  Award, 
  ArrowRight, 
  ClipboardCheck, 
  TrendingUp,
  Search,
  Calendar,
  CheckCircle2,
  XCircle
} from "lucide-react";
import { Toaster } from 'react-hot-toast';

export default function MyResults() {
  const [user, setUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const res = await getMySubmissions();
      setSubmissions(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    setUser(jwtDecode(token));
    loadData();
  }, [loadData]);

  const filtered = submissions.filter(sub => 
    sub.exam_id.toString().includes(search) || 
    (sub.passed ? "pass" : "fail").includes(search.toLowerCase())
  );

  if (loading || !user) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-950">
      <div className="w-10 h-10 border-4 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="w-full">
      <Toaster position="top-center" />
      
      <main className="flex-1 p-6 lg:p-10 space-y-12 pb-20 min-w-0">
        {/* Header */}
        <header>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full text-xs font-black mb-6 uppercase tracking-widest">
              <Trophy size={14} fill="currentColor" /> Achievement Timeline
            </div>
            <h1 className="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white leading-tight tracking-tight">
              Exam <span className="text-indigo-600">Performance</span>
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-4 text-lg font-medium">
              Analyze your growth and review your detailed performance reports.
            </p>
          </motion.div>
        </header>

        {/* Stats Grid */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
           <div className="p-8 bg-slate-50 dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800">
              <TrendingUp className="text-indigo-600 mb-4" size={32} />
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Exams</p>
              <p className="text-3xl font-black text-slate-900 dark:text-white">{submissions.length}</p>
           </div>
           <div className="p-8 bg-slate-50 dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800">
              <CheckCircle2 className="text-emerald-500 mb-4" size={32} />
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Passed</p>
              <p className="text-3xl font-black text-slate-900 dark:text-white">{submissions.filter(s => s.passed).length}</p>
           </div>
           <div className="p-8 bg-slate-50 dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800">
              <XCircle className="text-red-500 mb-4" size={32} />
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Average Score</p>
              <p className="text-3xl font-black text-slate-900 dark:text-white">
                {submissions.length > 0 
                  ? Math.round(submissions.reduce((acc, curr) => acc + curr.percentage, 0) / submissions.length)
                  : 0}%
              </p>
           </div>
        </section>

        {/* Search & Filter */}
        <div className="relative">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input 
            type="text" 
            placeholder="Search by Exam ID or Status..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-14 pr-8 py-5 bg-slate-50 dark:bg-slate-900 border-0 rounded-[2rem] outline-none focus:ring-2 focus:ring-indigo-600/20 transition-all font-bold text-slate-800 dark:text-white"
          />
        </div>

        {/* Results List */}
        <section className="space-y-6">
          {filtered.length === 0 ? (
            <div className="p-20 bg-slate-50 dark:bg-slate-900 rounded-[3rem] text-center border-2 border-dashed border-slate-200 dark:border-slate-800">
              <ClipboardCheck className="mx-auto mb-6 text-slate-300 dark:text-slate-700" size={64} />
              <p className="text-slate-500 font-black uppercase tracking-widest">No results found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((sub, i) => (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                  whileHover={{ y: -5 }}
                  key={sub.id} 
                  className="p-8 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[3rem] shadow-sm hover:shadow-2xl transition-all relative overflow-hidden group"
                >
                  <div className={`absolute top-0 right-0 w-32 h-32 blur-3xl opacity-10 transition-opacity group-hover:opacity-30 ${sub.passed ? "bg-emerald-500" : "bg-red-500"}`} />
                  
                  <div className="flex items-center justify-between mb-8">
                    <div className={`p-4 rounded-2xl ${sub.passed ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"} dark:bg-slate-800`}>
                      {sub.passed ? <CheckCircle2 size={24} /> : <XCircle size={24} />}
                    </div>
                    <span className={`text-[10px] font-black uppercase tracking-[0.2em] px-4 py-1.5 rounded-full ${sub.passed ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
                      {sub.passed ? "Qualified" : "Not Qualified"}
                    </span>
                  </div>

                  <div className="mb-8">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Submission ID: #{sub.id}</p>
                    <h4 className="text-3xl font-black text-slate-900 dark:text-white">Exam #{sub.exam_id}</h4>
                  </div>

                  <div className="flex items-center gap-6 mb-10">
                     <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter mb-1">Score</p>
                        <p className="text-2xl font-black text-indigo-600">{sub.percentage}%</p>
                     </div>
                     <div className="w-px h-8 bg-slate-100 dark:bg-slate-800" />
                     <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter mb-1">Correct</p>
                        <p className="text-2xl font-black text-slate-900 dark:text-white">{sub.score}/{sub.total_questions}</p>
                     </div>
                  </div>

                  <div className="flex items-center justify-between pt-8 border-t border-slate-50 dark:border-slate-800">
                    <div className="flex items-center gap-2 text-slate-400 text-xs font-bold">
                       <Calendar size={14} /> {new Date(sub.submitted_at).toLocaleDateString()}
                    </div>
                    <button 
                      onClick={() => navigate(`/exam-result/${sub.id}`)}
                      className="w-12 h-12 bg-slate-900 dark:bg-indigo-600 text-white rounded-2xl flex items-center justify-center hover:bg-indigo-700 transition-all shadow-xl shadow-slate-200 dark:shadow-none"
                    >
                      <ArrowRight size={20} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
